/**
 * ClassOne Inner Navigation — scroll-spy + sticky.
 *
 * Each .c1w-inner-navigation instance is initialised independently so multiple
 * navs on the same page work without interference.
 *
 * When body has overflow:hidden (custom smooth-scroll themes), window.scrollY
 * is always 0. findScrollContainer() walks up the DOM to find the actual
 * scrolling element and uses that for all scroll event listeners and calculations.
 */

document.addEventListener( 'DOMContentLoaded', () => {
	document.querySelectorAll( '.c1w-inner-navigation' ).forEach( ( nav ) => {
		const { getNavBottom, scrollTarget } = initSticky( nav );
		initScrollSpy( nav, getNavBottom, scrollTarget );
	} );
} );

// ── Scroll container detection ────────────────────────────────────────────────

function findScrollContainer( el ) {
	let node = el.parentElement;
	while ( node && node !== document.documentElement ) {
		const { overflowY } = window.getComputedStyle( node );
		if ( /auto|scroll/.test( overflowY ) ) return node;
		node = node.parentElement;
	}
	return window;
}

// Unified scroll helpers — work whether target is window or a DOM element.
function getScrollY( target )      { return target === window ? window.scrollY      : target.scrollTop;    }
function getScrollH( target )      { return target === window ? document.documentElement.scrollHeight : target.scrollHeight; }
function getClientH( target )      { return target === window ? window.innerHeight  : target.clientHeight; }
function scrollTo( target, y )     { target.scrollTo( { top: y, behavior: 'smooth' } ); }

// ── Scroll-spy ────────────────────────────────────────────────────────────────
//
// Marks the button whose target section most recently crossed the activation
// line (viewport-top × spyLine%). The last button is forced active at
// the bottom of the scroll container. Anchor-type buttons only.
//
// getNavBottom() returns the nav's live bottom edge in the viewport; used as
// the scroll offset on click so the target lands immediately below the nav.

function initScrollSpy( nav, getNavBottom, scrollTarget ) {
	const spyLine       = parseInt( nav.dataset.spyLine    ?? '30', 10 );
	const initialActive = nav.dataset.initialActive         ?? 'first';

	const buttons = Array.from(
		nav.querySelectorAll( '.c1w-inner-navigation__button[data-link-type="anchor"]' )
	);

	if ( buttons.length === 0 ) return;

	const targets = buttons.map( ( btn ) => document.getElementById( btn.dataset.target ?? '' ) );

	// Scroll-lock: clicked button stays active during smooth-scroll animation.
	// Lifts 150 ms after the last scroll event from the animation.
	let scrollLock = false;
	let lockTimer  = null;

	function releaseLock() {
		scrollLock = false;
		lockTimer  = null;
		setActive( getActiveIndex() );
	}

	buttons.forEach( ( btn, i ) => {
		btn.addEventListener( 'click', ( e ) => {
			const el = targets[ i ];
			if ( ! el ) return;
			e.preventDefault();

			const rawOffset = btn.dataset.offset;
			const offset    = rawOffset !== undefined
				? parseInt( rawOffset, 10 )
				: getNavBottom();

			// getBoundingClientRect().top is viewport-relative regardless of scroll container.
			const y = el.getBoundingClientRect().top + getScrollY( scrollTarget ) - offset;

			setActive( i );
			scrollLock = true;
			clearTimeout( lockTimer );

			scrollTo( scrollTarget, y );
		} );
	} );

	function getActiveIndex() {
		const scrollY  = getScrollY( scrollTarget );
		const clientH  = getClientH( scrollTarget );
		const scrollH  = getScrollH( scrollTarget );
		const atBottom = scrollY + clientH >= scrollH - 4;
		if ( atBottom ) return buttons.length - 1;

		const line   = clientH * spyLine / 100;
		let   active = initialActive === 'first' ? 0 : -1;

		targets.forEach( ( el, i ) => {
			if ( ! el ) return;
			if ( el.getBoundingClientRect().top <= line ) active = i;
		} );

		return active;
	}

	function setActive( index ) {
		buttons.forEach( ( btn, i ) => {
			const on = i === index;
			btn.classList.toggle( 'is-active', on );
			btn.setAttribute( 'aria-current', on ? 'true' : 'false' );
		} );
	}

	setActive( getActiveIndex() );

	let ticking = false;
	scrollTarget.addEventListener(
		'scroll',
		() => {
			if ( scrollLock ) {
				clearTimeout( lockTimer );
				lockTimer = setTimeout( releaseLock, 150 );
				return;
			}
			if ( ticking ) return;
			ticking = true;
			requestAnimationFrame( () => {
				setActive( getActiveIndex() );
				ticking = false;
			} );
		},
		{ passive: true }
	);
}

// ── Sticky ────────────────────────────────────────────────────────────────────
//
// Uses CSS position:sticky. The browser handles sticking natively within the
// scroll container found by findScrollContainer().
//
// 'top' keyword → stick at top: 0.
// CSS selector  → stick at the bottom edge of the matched element (e.g. a header).
//
// Returns { getNavBottom, scrollTarget }:
//   getNavBottom — nav's live bottom edge in viewport; used by scroll-spy on click.
//   scrollTarget — the actual scrolling element; passed to initScrollSpy.

function initSticky( nav ) {
	const stickySelector = ( nav.dataset.stickySelector ?? '' ).trim();
	const stickyToTop    = stickySelector === 'top';
	const baseEl         = ( stickySelector && ! stickyToTop ) ? document.querySelector( stickySelector ) : null;

	const scrollTarget = findScrollContainer( nav );

	if ( ! stickySelector ) {
		return { getNavBottom: () => 0, scrollTarget };
	}

	function getStickyTop() {
		if ( ! baseEl ) return 0;
		return Math.max( 0, baseEl.getBoundingClientRect().bottom );
	}

	nav.classList.add( 'is-sticky-enabled' );
	nav.style.top = getStickyTop() + 'px';

	window.addEventListener( 'resize', () => {
		nav.style.top = getStickyTop() + 'px';
	} );

	// Always calculate offset as if the nav were already sticky.
	// getBoundingClientRect().bottom fluctuates during the non-sticky → sticky
	// transition, making the first-click calculation land in the wrong position.
	// getStickyTop() + offsetHeight is stable regardless of current scroll state.
	return { getNavBottom: () => getStickyTop() + nav.offsetHeight, scrollTarget };
}
